import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { bookingAPI } from '../services/api';
import { 
  ArrowLeft, User, Phone, Calendar, Home, CreditCard,
  Loader2, LogIn, LogOut, X, CheckCircle
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const BookingDetailScreen = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    const fetchBooking = async () => {
      try {
        const response = await bookingAPI.getBooking(id);
        if (response.STATUS === 1) {
          setBooking(response.RESULT);
        }
      } catch (error) {
        console.error('Error fetching booking:', error);
        toast({
          title: 'Error',
          description: 'Failed to load booking details',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchBooking();
  }, [id, toast]);

  const handleCheckIn = async () => {
    setActionLoading(true);
    try {
      const response = await bookingAPI.checkIn(id);
      if (response.STATUS === 1) {
        setBooking(response.RESULT);
        toast({ title: 'Success', description: 'Guest checked in successfully' });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error?.detail || 'Failed to check in',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleCheckOut = async () => {
    setActionLoading(true);
    try {
      const response = await bookingAPI.checkOut(id);
      if (response.STATUS === 1) {
        setBooking(response.RESULT);
        toast({ title: 'Success', description: 'Guest checked out successfully' });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error?.detail || 'Failed to check out',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) return;
    
    setActionLoading(true);
    try {
      const response = await bookingAPI.rejectBooking(id, 'Cancelled by user');
      if (response.STATUS === 1) {
        setBooking(response.RESULT);
        toast({ title: 'Success', description: 'Booking cancelled' });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error?.detail || 'Failed to cancel booking',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusInfo = () => {
    if (!booking) return { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Unknown' };
    
    const status = booking.booking_status;
    if (status === 'cancelled' || status === 'rejected') {
      return { bg: 'bg-red-100', text: 'text-red-700', label: 'Cancelled' };
    }
    if (booking.is_checked_out || status === 'checked_out') {
      return { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Checked Out' };
    }
    if (booking.is_checked_in || status === 'checked_in') {
      return { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Checked In' };
    }
    return { bg: 'bg-[#EEF1FC]', text: 'text-[#1B2A7B]', label: 'Confirmed' };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#1B2A7B] animate-spin" />
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
        <p className="text-gray-500 mb-4">Booking not found</p>
        <button
          onClick={() => navigate('/')}
          className="px-4 py-2 bg-[#1B2A7B] text-white rounded-lg hover:bg-[#1B2A7B]-dark transition-colors"
        >
          Back to Calendar
        </button>
      </div>
    );
  }

  const statusInfo = getStatusInfo();
  const canCheckIn = !booking.is_checked_in && booking.booking_status !== 'cancelled';
  const canCheckOut = booking.is_checked_in && !booking.is_checked_out;
  const canCancel = !booking.is_checked_in && booking.booking_status !== 'cancelled';

  return (
    <div className="min-h-screen bg-gray-50 pb-32" data-testid="booking-detail-screen">
      {/* Header */}
      <div className="bg-navy px-4 py-4 shadow-md">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-lg bg-navy-light text-white hover:bg-navy-dark transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-semibold text-white">Booking Details</h1>
            <p className="text-[#1B2A7B] text-sm">{booking.booking_id}</p>
          </div>
          <span className={`px-3 py-1.5 rounded-lg text-xs font-medium ${statusInfo.bg} ${statusInfo.text}`}>
            {statusInfo.label}
          </span>
        </div>
      </div>

      <div className="p-4 space-y-4 max-w-lg mx-auto">
        {/* Guest Info */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <User className="w-4 h-4 text-[#1B2A7B]" />
            Guest Information
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-500 text-sm">Guest Name</span>
              <span className="font-medium text-gray-900">{booking.guest_name}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-500 text-sm">Mobile</span>
              <span className="font-medium text-gray-900 flex items-center gap-2">
                <Phone className="w-4 h-4 text-gray-400" />
                {booking.mobile_number}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-500 text-sm">Source</span>
              <span className="font-medium text-[#1B2A7B]">{booking.booking_source}</span>
            </div>
          </div>
        </div>

        {/* Stay Dates */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <Calendar className="w-4 h-4 text-[#1B2A7B]" />
            Stay Dates
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-3 bg-[#EEF1FC] rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Check-in</p>
              <p className="font-semibold text-gray-900">
                {(booking.check_in_date || booking.check_in || '').split('T')[0]}
              </p>
            </div>
            <div className="text-center p-3 bg-gray-100 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Check-out</p>
              <p className="font-semibold text-gray-900">
                {(booking.check_out_date || booking.check_out || '').split('T')[0]}
              </p>
            </div>
          </div>
        </div>

        {/* Rooms */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <Home className="w-4 h-4 text-[#1B2A7B]" />
            Rooms
          </h3>
          <div className="space-y-2">
            {booking.rooms && booking.rooms.length > 0 ? booking.rooms.map((room, index) => (
              <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-900">{room.room_name || room.room_type_name || 'Room'}</span>
                <span className="text-[#1B2A7B] font-medium">x{room.no_of_rooms || room.room_count || 1}</span>
              </div>
            )) : (
              <div className="p-3 bg-gray-50 rounded-lg text-gray-900" data-testid="booking-rooms-fallback">
                {booking.room_type ? `${booking.room_type} x${booking.no_of_rooms || 1}` : 'N/A'}
              </div>
            )}
          </div>
        </div>

        {/* Payment */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <CreditCard className="w-4 h-4 text-[#1B2A7B]" />
            Payment
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-500 text-sm">Mode</span>
              <span className="font-medium text-gray-900">{booking.payment_mode || 'Cash'}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-500 text-sm">Amount</span>
              <span className="text-xl font-bold text-[#1B2A7B]">
                ₹{(booking.total_amount || booking.total_price || booking.a_property_gross_charges || booking.payment_amount || 0).toLocaleString()}
              </span>
            </div>
          </div>
        </div>

        {/* Check-in/Check-out Times */}
        {(booking.check_in_time || booking.check_out_time) && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-[#1B2A7B]" />
              Activity
            </h3>
            <div className="space-y-3">
              {booking.check_in_time && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-500 text-sm">Checked In</span>
                  <span className="font-medium text-gray-900">
                    {new Date(booking.check_in_time).toLocaleString()}
                  </span>
                </div>
              )}
              {booking.check_out_time && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-500 text-sm">Checked Out</span>
                  <span className="font-medium text-gray-900">
                    {new Date(booking.check_out_time).toLocaleString()}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg">
        <div className="max-w-lg mx-auto flex gap-3">
          {canCancel && (
            <button
              onClick={handleCancel}
              disabled={actionLoading}
              className="flex-1 py-3 bg-red-50 text-red-600 font-medium rounded-xl hover:bg-red-100 disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
              data-testid="cancel-button"
            >
              <X className="w-5 h-5" />
              Cancel
            </button>
          )}
          
          {canCheckIn && (
            <button
              onClick={handleCheckIn}
              disabled={actionLoading}
              className="flex-1 py-3 bg-[#1B2A7B] text-white font-medium rounded-xl hover:bg-[#1B2A7B]-dark disabled:opacity-50 flex items-center justify-center gap-2 transition-colors shadow-[#1B2A7B]/30"
              data-testid="check-in-button"
            >
              {actionLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <LogIn className="w-5 h-5" />
                  Check In
                </>
              )}
            </button>
          )}
          
          {canCheckOut && (
            <button
              onClick={handleCheckOut}
              disabled={actionLoading}
              className="flex-1 py-3 bg-navy text-white font-medium rounded-xl hover:bg-navy-dark disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
              data-testid="check-out-button"
            >
              {actionLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <LogOut className="w-5 h-5" />
                  Check Out
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingDetailScreen;
